
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { Icon } from '@/components/ui/icon'
import { streamingStore } from '../base/streaming-store'

interface TextFileProps {
  id: string
  title?: string
  selected?: boolean
  initialText?: string
  width?: number
  height?: number
  onSelect?: () => void
  onChange?: (text: string) => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
}

function TextFileComponent({
  id,
  title,
  selected = false,
  initialText = '',
  width,
  height,
  onSelect,
  onChange,
  onHoverChange,
  onStateChange,
  state = {},
}: TextFileProps) {
  const [text, setText] = useState(initialText)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Track initial mount to skip first onChange
  const isInitialMount = useRef(true)
  useEffect(() => {
    isInitialMount.current = false
  }, [])

  // Stream text from lightweight store during generation (avoids React Flow state updates)
  const generationStatus = state?.generationStatus as string | undefined
  const [streamingText, setStreamingText] = useState('')
  const prevGenerationStatus = useRef(generationStatus)

  useEffect(() => {
    if (generationStatus === 'loading') {
      // Subscribe to streaming store for real-time token updates
      setStreamingText(streamingStore.get(id))
      const unsub = streamingStore.subscribe(id, setStreamingText)
      return unsub
    }
    if (prevGenerationStatus.current === 'loading' && generationStatus === 'complete' && initialText) {
      setText(initialText)
      setStreamingText('')
    }
    prevGenerationStatus.current = generationStatus
  }, [generationStatus, initialText, id])

  // Auto-resize textarea based on content
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate new height using schema constants
    const minHeight = 160
    const maxHeight = 420  // Fits within 500px square (500 - title 28px - padding ~52px)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
    textarea.style.height = `${newHeight}px`

    // Node height is 'auto', so it will grow with textarea automatically
  }, [])

  // Debounced resize for text-change (avoids synchronous reflow on every keystroke)
  const debouncedResize = useCallback(() => {
    const timeoutId = setTimeout(() => {
      resizeTextarea()
    }, 150)
    return () => clearTimeout(timeoutId)
  }, [resizeTextarea])

  // Resize on text change (debounced)
  useEffect(() => {
    const cleanup = debouncedResize()
    return cleanup
  }, [text, debouncedResize])

  // Initial resize
  useEffect(() => {
    resizeTextarea()
  }, [resizeTextarea])

  // Resize when node expands (collapsed state changes)
  useEffect(() => {
    // When node is expanded (collapsed: false), re-calculate textarea height
    if (state.collapsed === false) {
      // Use requestAnimationFrame to ensure DOM has reflowed
      requestAnimationFrame(() => {
        resizeTextarea()
      })
    }
  }, [state.collapsed, resizeTextarea])

  // Notify parent via useEffect (not in setState)
  useEffect(() => {
    if (isInitialMount.current) return
    onChange?.(text)
  }, [text, onChange])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
  }, [])

  return (
    <NodeContainer
      nodeId={id}
      title={title || "Text"}
      icon="file-text"
      selected={selected}
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="relative flex-1 pt-2 pr-2 pb-1 pl-2">
        {(state?.generationStatus as string) === 'loading' ? (
          <div className="relative w-full" style={{ minHeight: '160px', maxHeight: '420px' }}>
            <div
              className="w-full whitespace-pre-wrap break-words pt-1 pl-1 text-sm text-foreground overflow-y-auto scrollbar-thin"
              style={{ minHeight: '160px', maxHeight: '420px' }}
            >
              {streamingText || (
                <span className="text-muted-foreground flex items-center gap-2">
                  <span className="inline-block w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Generating...
                </span>
              )}
              <span className="inline-block w-[2px] h-[1em] bg-primary animate-pulse ml-[1px] align-text-bottom" />
            </div>
          </div>
        ) : (state?.generationStatus as string) === 'error' ? (
          <div className="flex flex-col w-full items-center justify-center gap-2 py-12 rounded-[10px] bg-muted">
            <div role="alert" className="space-y-2 px-4 text-center text-destructive">
              <p className="text-sm font-medium">Generation failed</p>
              <p className="text-xs break-words">{typeof state.generationError === 'string' ? state.generationError : 'No error details were saved for this output. Retry from the source text node.'}</p>
            </div>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            placeholder="Enter text..."
            spellCheck={false}
            className="w-full resize-none border-0 bg-transparent pt-1 pl-1 text-sm outline-none placeholder:text-muted-foreground overflow-y-auto scrollbar-thin"
            style={{
              minHeight: '160px',
              maxHeight: '420px'
            }}
            onClick={(e) => {
              if (selected) {
                e.stopPropagation()
              }
            }}
          />
        )}
      </CardContent>
    </NodeContainer>
  )
}

// Export optimized version
export const TextFile = withNodeOptimization(TextFileComponent, {
  compareProps: ['id', 'title', 'selected', 'initialText', 'state']
})
